/**
 * SimulatoreGuadagno — modale di simulazione preventivo.
 *
 * Applica la stessa formula usata in /api/cantieri/{id}/economics:
 *   Ricavi = Contratto + Extra
 *   Costi  = Ore lavorate × €/h + Materiali + 17% × Ricavi
 *   Guadagno lordo = Ricavi − Costi
 *   Guadagno netto = Guadagno lordo × (1 − 27%)
 */
import React, { useState, useMemo } from "react";
import { X, Calculator, TrendingUp, TrendingDown } from "lucide-react";

const eur = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 2 })
    .format(Number(n) || 0);

const num = (v) => {
  if (v === "" || v == null) return 0;
  const n = parseFloat(String(v).replace(",", "."));
  return isNaN(n) ? 0 : n;
};

function Input({ label, hint, value, onChange, testid, suffix = "" }) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</label>
      <div className="relative">
        <input
          type="number"
          step="0.01"
          min="0"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full px-3 py-2 border-2 border-slate-300 focus:border-orange-500 rounded-sm font-mono text-right pr-10"
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 font-semibold">{suffix}</span>}
      </div>
      {hint && <div className="text-[10px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function Row({ label, value, negative, muted, strong, bold, testid, className = "" }) {
  return (
    <tr className={className}>
      <td className={`py-2 ${strong ? "font-display font-bold text-lg" : ""} ${muted ? "text-slate-500 text-xs" : "text-slate-800"}`}>
        {label}
      </td>
      <td
        data-testid={testid}
        className={`py-2 text-right font-mono ${bold ? "font-black text-xl" : ""} ${negative ? "text-rose-700" : strong ? "text-emerald-700" : ""}`}
      >
        {negative && "− "}{eur(value)}
      </td>
    </tr>
  );
}

export default function SimulatoreGuadagno({ open, onClose }) {
  const [contratto, setContratto] = useState("");
  const [extra, setExtra] = useState("");
  const [ore, setOre] = useState("");
  const [costoOra, setCostoOra] = useState("25");
  const [materiali, setMateriali] = useState("");
  const [aliquotaTasse, setAliquotaTasse] = useState("27");
  const [aliquotaFiscale, setAliquotaFiscale] = useState("17");

  const calc = useMemo(() => {
    const ricavi = num(contratto) + num(extra);
    const costoManodopera = num(ore) * num(costoOra);
    const costoMateriali = num(materiali);
    const imposte17 = ricavi * (num(aliquotaFiscale) / 100);
    const costiTot = costoManodopera + costoMateriali + imposte17;
    const guadagnoLordo = ricavi - costiTot;
    const tasse27 = Math.max(guadagnoLordo, 0) * (num(aliquotaTasse) / 100);
    const guadagnoNetto = guadagnoLordo - tasse27;
    const marginePct = ricavi > 0 ? (guadagnoNetto / ricavi) * 100 : 0;
    return { ricavi, costoManodopera, costoMateriali, imposte17, costiTot, guadagnoLordo, tasse27, guadagnoNetto, marginePct };
  }, [contratto, extra, ore, costoOra, materiali, aliquotaTasse, aliquotaFiscale]);

  if (!open) return null;
  const isRentable = calc.guadagnoNetto > 0;
  const isNoData = calc.ricavi === 0;

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-md shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="simulatore-guadagno-modal"
      >
        <div className="p-5 border-b border-slate-200 sticky top-0 bg-white z-10 flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1 inline-flex items-center gap-1">
              <Calculator className="w-3.5 h-3.5" /> Simulatore preventivo
            </div>
            <h2 className="font-display text-xl font-black">Guadagno atteso</h2>
            <div className="text-xs text-slate-500 mt-1">
              Inserisci contratto e costi stimati per calcolare il guadagno prima di aprire il cantiere.
              I dati non vengono salvati.
            </div>
          </div>
          <button onClick={onClose} data-testid="btn-close-simulatore"><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        <div className="p-5 grid lg:grid-cols-2 gap-6">
          {/* Input */}
          <div className="space-y-4">
            <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Ricavi</div>
            <Input label="Importo contratto" hint="Prezzo pattuito col cliente" value={contratto} onChange={setContratto} suffix="€" testid="sim-contratto" />
            <Input label="Extra / varianti" hint="Lavori extra fuori contratto" value={extra} onChange={setExtra} suffix="€" testid="sim-extra" />

            <div className="text-[10px] font-bold uppercase tracking-widest text-rose-700 pt-3 border-t border-slate-200">Costi stimati</div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="N. ore lavorate" hint="Manodopera totale prevista" value={ore} onChange={setOre} suffix="h" testid="sim-ore" />
              <Input label="Costo orario aziendale" hint="Media €/h (default 25)" value={costoOra} onChange={setCostoOra} suffix="€/h" testid="sim-costo-ora" />
            </div>
            <Input label="Materiali" hint="Bolle previste (imponibile)" value={materiali} onChange={setMateriali} suffix="€" testid="sim-materiali" />

            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 pt-3 border-t border-slate-200">Aliquote (modificabili)</div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Trattenute fiscali su ricavi" value={aliquotaFiscale} onChange={setAliquotaFiscale} suffix="%" testid="sim-fiscale" />
              <Input label="Tassazione sul guadagno lordo" value={aliquotaTasse} onChange={setAliquotaTasse} suffix="%" testid="sim-tasse" />
            </div>
          </div>

          {/* Output */}
          <div className="bg-slate-50 border-2 border-slate-900 shadow-brutal p-5">
            <div className="overline mb-3">Calcolo in tempo reale</div>
            {isNoData ? (
              <div className="text-center py-12 text-slate-400 text-sm">
                Inserisci almeno il <b>Contratto</b> per vedere il guadagno stimato
              </div>
            ) : (
              <>
                <table className="w-full text-sm">
                  <tbody>
                    <Row label="Contratto" value={num(contratto)} />
                    <Row label="Extra" value={num(extra)} />
                    <tr className="border-t border-slate-300">
                      <td className="py-2 font-bold">Ricavi totali</td>
                      <td className="py-2 text-right font-mono font-bold text-emerald-700" data-testid="sim-ricavi">{eur(calc.ricavi)}</td>
                    </tr>
                    <tr><td colSpan={2} className="pt-4 pb-1 text-[10px] font-bold uppercase tracking-widest text-rose-700">Costi da sottrarre</td></tr>
                    <Row label={`Ore lavorate (${num(ore)}h × ${eur(num(costoOra))}/h)`} value={calc.costoManodopera} negative />
                    <Row label="Materiali" value={calc.costoMateriali} negative />
                    <Row label={`${num(aliquotaFiscale)}% dei ricavi (trattenute fiscali)`} value={calc.imposte17} negative />
                    <tr className="border-t-2 border-slate-900">
                      <td className="py-3 font-display font-bold text-lg">Guadagno lordo</td>
                      <td
                        data-testid="sim-guadagno-lordo"
                        className={`py-3 text-right font-mono font-black text-lg ${calc.guadagnoLordo >= 0 ? "text-emerald-700" : "text-rose-700"}`}
                      >
                        {eur(calc.guadagnoLordo)}
                      </td>
                    </tr>
                    <Row label={`Tassazione ${num(aliquotaTasse)}% sul lordo`} value={calc.tasse27} negative />
                    <tr className={`border-t-2 ${isRentable ? "border-emerald-700 bg-emerald-100" : "border-rose-700 bg-rose-100"}`}>
                      <td className="py-3 font-display font-black text-lg">GUADAGNO NETTO</td>
                      <td
                        data-testid="sim-guadagno-netto"
                        className={`py-3 text-right font-mono font-black text-xl ${isRentable ? "text-emerald-800" : "text-rose-800"}`}
                      >
                        {eur(calc.guadagnoNetto)}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className={`mt-4 p-3 rounded-sm text-sm flex items-center gap-2 ${isRentable ? "bg-emerald-50 text-emerald-900" : "bg-rose-50 text-rose-900"}`}>
                  {isRentable
                    ? <><TrendingUp className="w-5 h-5" /> <span>Margine netto: <b>{calc.marginePct.toFixed(1)}%</b> — cantiere in profitto</span></>
                    : <><TrendingDown className="w-5 h-5" /> <span>Attenzione: cantiere <b>in perdita</b> con questi parametri</span></>
                  }
                </div>
                <div className="mt-3 text-[11px] text-slate-500 font-mono leading-relaxed">
                  Formula: Ricavi − (Ore×€/h + Materiali + {num(aliquotaFiscale)}%×Ricavi) → GL × (1 − {num(aliquotaTasse)}%) = GN
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
